import {
  AGE_65,
  AGE_75,
  AGE_100,
  ALLOWED_TRAVEL_LIMITS_BY_REGION,
  ALLOWED_TRAVEL_PLANS_BY_REGION,
  BRITAIN_COUNTRY_ID,
  DEFAULT_SELECTED_LIMIT,
  DEFAULT_SELECTED_TRAVEL_PLAN,
  DISALLOWED_LITE_COUNTRIES_SHORT_CODES,
  GEORGIA_REGION_ID,
  REGIONS,
  SCHENGEN_REGION_ID,
  TRAVEL_LIMITS,
  TRAVEL_PLANS,
  TRAVEL_PLANS_BETWEEN_AGES_65_75,
  TRAVEL_PLANS_BETWEEN_AGES_75_100,
  TRAVEL_PLANS_LABEL,
  TRAVEL_PLANS_WITH_MAX_AGE_65,
  TRAVEL_PRICE_KEYS,
  TRAVEL_SPECIAL_COUNTRIES,
  WORLDWIDE_REGION_ID,
} from '../../constants/travelCalcConstants';
import { getActiveCoveragesFromPlan, getCalculatorDiscountedPrice, getSummaryPricesByKeyFromPlan } from '../price';
import { bindActiveCoverages, bindContractSummary } from './bindData';

export const planMaxAgeIsAllowed = (planName, age) => {
  if (age <= AGE_65) {
    return TRAVEL_PLANS_WITH_MAX_AGE_65.includes(planName);
  }
  if (age > AGE_65 && age <= AGE_75) {
    return TRAVEL_PLANS_BETWEEN_AGES_65_75.includes(planName);
  }
  if (age > AGE_75 && age <= AGE_100) {
    return TRAVEL_PLANS_BETWEEN_AGES_75_100.includes(planName);
  }
  return false;
};

export const planHasAllowedRegion = (planName, region) => ALLOWED_TRAVEL_PLANS_BY_REGION[region]
  .includes(planName);

export const planHasAllowedLimit = (plan, region) => {
  const allowedLimitIds = ALLOWED_TRAVEL_LIMITS_BY_REGION[region] || [];
  return plan.limits.some((l) => allowedLimitIds.includes(l.id));
};

export const isLimitValidForRegion = (lim, reg) => ALLOWED_TRAVEL_LIMITS_BY_REGION[reg]
  .includes(lim.id);

export const getTravelAvailableLimits = (plans, selectedPlan, region) => {
  const filteredPlan = plans.find((plan) => plan.label === selectedPlan);

  if (!filteredPlan) {
    return [];
  }

  const isGeorgiaPlan = filteredPlan.label === TRAVEL_PLANS.GEORGIA.label;
  const filterRegion = isGeorgiaPlan ? TRAVEL_PLANS_LABEL.GEORGIA : region;

  return filteredPlan.limits.filter((plan) => isLimitValidForRegion(plan, filterRegion));
};

export const getQuoteRequests = (
  plans,
  selectedLimit,
  selectedCoverages,
  age,
  selectedPlan,
) => {
  const plan = plans.find((item) => item.label === selectedPlan);

  const coverages = Object.keys(plan.coverages).map((key) => ({
    id: plan.coverages[key].id,
    active: !!selectedCoverages && !!selectedCoverages[key] && selectedCoverages[key].active,
  }));

  return [{
    package: plan.label,
    amount: selectedLimit.value,
    currency: selectedLimit.currency,
    coverages,
  }];
};

export const getRegionFromDestinations = (destinations) => {
  if (!destinations || !destinations.length) {
    return null;
  }
  // if destinations include US, Canada, Australia, Japan or Great Britain,
  // region should be set to world_wide
  const isWorldWide = (destination) => destination.region.id === WORLDWIDE_REGION_ID
    || destination.country.id === BRITAIN_COUNTRY_ID;

  const isGeorgiaCountry = (destination) => destination.region.id === GEORGIA_REGION_ID;

  const isSchengen = (destination) => destination.region.id === SCHENGEN_REGION_ID;

  if (destinations.some(isWorldWide)) {
    return REGIONS.WW;
  }

  if (destinations.every(isSchengen)) {
    return REGIONS.SCH;
  }

  if (destinations.some(isSchengen)) {
    return REGIONS.WW;
  }

  if (destinations.every(isGeorgiaCountry)) {
    return REGIONS.GEORGIA;
  }

  return REGIONS.WW_EXC;
};

export const getLimitOnRegionChange = (newRegion) => {
  if (!newRegion) {
    return {};
  }
  const key = ALLOWED_TRAVEL_LIMITS_BY_REGION[newRegion][0];
  return TRAVEL_LIMITS[key];
};

export const getDefaultPlanForRegion = (region) => {
  const key = DEFAULT_SELECTED_TRAVEL_PLAN[region];
  return TRAVEL_PLANS[key];
};

export const getDefaultLimitForRegion = (region) => {
  const key = DEFAULT_SELECTED_LIMIT[region];
  return TRAVEL_LIMITS[key];
};

export const getLimitByValueAndCurrency = (val, curr) => Object.values(TRAVEL_LIMITS)
  .find((limit) => {
    const { value, currency } = limit;
    return value === +val && curr.toLowerCase() === currency.toLowerCase();
  });

export const getActiveCoverages = (prices, t, selectedPlan, isFetchingPrice) => {
  const planId = TRAVEL_PLANS[selectedPlan]?.id;
  const planPrices = prices[planId];
  if (planId && planPrices) {
    return bindActiveCoverages(
      getActiveCoveragesFromPlan(Object.values(planPrices)), t, isFetchingPrice,
    );
  }
  return [];
};

export const getActiveCoveragesCount = (prices, selectedPlanId) => {
  const planPrices = prices[selectedPlanId];
  if (planPrices) {
    return Object.keys(getActiveCoveragesFromPlan(Object.values(planPrices))).length;
  }
  return '-';
};

export const getContractSummary = (
  prices,
  t,
  selectedPlan,
  discountPrice,
  isFetchingPrice,
  travelers,
  language,
  classes,
  handleOpenTravelersModal,
) => {
  if (prices[selectedPlan]) {
    const plan = Object.values(prices[selectedPlan]);
    const base = getSummaryPricesByKeyFromPlan(plan, TRAVEL_PRICE_KEYS.BASE);
    const sum = getSummaryPricesByKeyFromPlan(plan, TRAVEL_PRICE_KEYS.SUM);
    return bindContractSummary(
      { ...base, ...sum },
      selectedPlan,
      t,
      discountPrice,
      isFetchingPrice,
      travelers,
      language,
      classes,
      handleOpenTravelersModal,
    );
  }
  return bindContractSummary(
    { base: 0, sum: 0 },
    selectedPlan,
    t,
    discountPrice,
    isFetchingPrice,
    travelers,
    language,
    classes,
    handleOpenTravelersModal,
  );
};

export const getDiscountedPrice = (policyPrices, selectedPlan) => {
  if (policyPrices[selectedPlan]) {
    return getCalculatorDiscountedPrice(policyPrices[selectedPlan].discountedPrices);
  }
  return 0;
};

export const getPlansWithAllowedLimitRegionAndAge = (
  region,
  age,
  hasOnlyLiteDisallowedCountry,
) => {
  let selectedPlans;
  if (hasOnlyLiteDisallowedCountry) {
    selectedPlans = Object.values(TRAVEL_PLANS).filter(
      (plan) => plan.id !== TRAVEL_PLANS_LABEL.LITE,
    );
    return selectedPlans.filter(
      (plan) => planHasAllowedLimit(plan, region)
            && planMaxAgeIsAllowed(plan.label, age),
    );
  }
  // When isn't selected disallowed country.
  selectedPlans = Object.values(TRAVEL_PLANS).filter(
    (plan) => plan.id !== TRAVEL_PLANS_LABEL.GEORGIA,
  );

  return selectedPlans.filter(
    (plan) => planHasAllowedLimit(plan, region)
      && planHasAllowedRegion(plan.label, region)
      && planMaxAgeIsAllowed(plan.label, age),
  );
};

export const getLiteDisallowedCountries = (selectedDestinations) => selectedDestinations?.some(
  (destination) => DISALLOWED_LITE_COUNTRIES_SHORT_CODES.includes(destination.code),
);

export const getOnlyLiteDisallowedCountry = (selectedDestinations) => (
  selectedDestinations?.length === 1
    ? getLiteDisallowedCountries(selectedDestinations)
    : false);

export const checkWhenDestinationOnlyGeorgia = (selectedDestinations) => (
    selectedDestinations?.length === 1
      ? (selectedDestinations[0].code === TRAVEL_SPECIAL_COUNTRIES.GEORGIA.code)
      : false
);

export const getSelectedRegionForSummaryContract = (selectedDestinations, region) => (
  checkWhenDestinationOnlyGeorgia(selectedDestinations)
    ? TRAVEL_SPECIAL_COUNTRIES.GEORGIA.name
    : region
);

export const getSelectedPlansWithAllowedLimitRegionAndAge = (
  plansWithAllowedLimitRegionAndAge,
  selectedPlan,
) => {
  if (plansWithAllowedLimitRegionAndAge.length && selectedPlan) {
    return plansWithAllowedLimitRegionAndAge.find((plan) => plan.id === selectedPlan);
  }
  return {};
};

export const getProgramColorByType = (type) => {
  switch (type) {
    case TRAVEL_PLANS.LITE.label:
      return '#2C8DD5';
    case TRAVEL_PLANS.GEORGIA.label:
      return '#D71313';
    case TRAVEL_PLANS.LITE_PLUS.label:
      return '#FFC30B';
    default:
      return '#40C181';
  }
};

export const getBasePrice = (prices, selectedPlan) => {
  if (prices[selectedPlan]) {
    const plan = Object.values(prices[selectedPlan]);
    const { base } = getSummaryPricesByKeyFromPlan(plan, TRAVEL_PRICE_KEYS.BASE);
    return base;
  }
  return 0;
};

export const bindTravelAdditionalCoverages = (selectedPlan) => (
  selectedPlan ? Object.values(TRAVEL_PLANS[selectedPlan].coverages) : []
);

export const bindTravelSelectedPlanCoverages = (
  selectedPlan,
) => {
  const object = {};
  // eslint-disable-next-line array-callback-return
  Object.values(TRAVEL_PLANS[selectedPlan].coverages).map((item) => {
    const { label } = item;
    object[label] = {
      id: item.id,
      active: item.active,
    };
  });

  return object;
};

export const bindTravelPlanDefaultCoveragesDescription = (
    currentPlan,
    translate,
) => currentPlan?.descriptions?.map((description) => ({
  ...description,
  label: translate(`travel.types.${currentPlan.label}.description${description}`),
}));
